import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Film, RefreshCw, Download, Eye } from "lucide-react";
import { devWarn } from "@/lib/devlog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VendorVideoPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/admin/vendor-video/status");
      setStatus(r.data);
      // Auto-poll while a render is in progress.
      if (r.data?.status === "running") {
        setPolling(true);
      } else {
        setPolling(false);
      }
    } catch (e) {
      // Best-effort.
      devWarn("vendor-video/status failed", e);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (!polling) return;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [polling]);

  const trigger = async () => {
    setBusy(true);
    try {
      await api.post("/admin/vendor-video/render");
      toast.success("Vendor video render queued — about 8 minutes.");
      setPolling(true);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const dlUrl = status?.exists ? `${BACKEND_URL}${status.url}` : null;

  const isRunning = status?.status === "running";
  const progress = Number(status?.progress || 0);

  return (
    <section
      className="mt-10 rounded-2xl border border-slate-200 bg-white p-6"
      data-testid="vendor-video-panel"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-blue-700">
            <Film className="w-3.5 h-3.5" /> Vendor partnership film
          </div>
          <h3 className="mt-1 font-display text-2xl font-bold text-slate-900 tracking-tight">
            Cinematic 2-minute B2B film for the public Vendors page.
          </h3>
          <p className="mt-1 text-sm text-slate-600 max-w-2xl leading-relaxed">
            7 cinematic scenes + CTA card · OpenAI TTS narration ("sage" voice, female founder-led) ·
            ambient pad · 1080p H.264 / AAC · real Nodera site screenshots inlined. Rendered output drops at
            <span className="font-mono text-[12px] mx-1">/api/static/demo-videos/vendor-partnership-latest.mp4</span>
            and embeds automatically on <span className="font-bold">/vendors</span>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refresh}
            disabled={isRunning}
            data-testid="vendor-video-refresh"
            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-60"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isRunning ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            type="button"
            onClick={trigger}
            disabled={busy || isRunning}
            data-testid="vendor-video-render"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-5 py-2 text-xs font-bold hover:bg-slate-800 disabled:opacity-60"
          >
            <Film className="w-3.5 h-3.5" />
            {isRunning ? `Rendering · ${progress}%` : busy ? "Queueing…" : status?.exists ? "Re-render" : "Render film"}
          </button>
        </div>
      </div>

      {/* Status strip */}
      <div className="mt-5 flex items-center gap-3 flex-wrap text-xs">
        {isRunning ? (
          <>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-1 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              Rendering · {progress}%
            </span>
            <div className="flex-1 max-w-md h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-amber-400 transition-all" style={{ width: `${progress}%` }} />
            </div>
          </>
        ) : status?.exists ? (
          <>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-1 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Live · {Math.round((status.size_bytes || 0) / 1024 / 1024)} MB
              {status.duration_sec ? ` · ${Math.round(status.duration_sec)}s` : ""}
              {status.scene_count ? ` · ${status.scene_count} scenes` : ""}
            </span>
            <span className="text-slate-500">
              Last rendered{" "}
              {status.finished_at
                ? new Date(status.finished_at).toLocaleString(undefined, {
                    year: "numeric", month: "short", day: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })
                : "—"}
            </span>
          </>
        ) : (
          <span className="text-slate-500">No render yet — click <b>Render film</b> to start.</span>
        )}
      </div>

      {/* Preview + actions */}
      {status?.exists && dlUrl && (
        <div className="mt-5 grid lg:grid-cols-12 gap-5">
          <div className="lg:col-span-7">
            <div className="rounded-xl overflow-hidden ring-1 ring-slate-200 bg-slate-950"
                 style={{ aspectRatio: "16 / 9" }}>
              <video
                key={dlUrl}
                src={dlUrl}
                className="w-full h-full"
                controls
                preload="metadata"
                data-testid="vendor-video-preview"
              />
            </div>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-2 text-sm">
            <a
              href={dlUrl}
              download
              data-testid="vendor-video-download"
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-slate-900 text-white px-4 py-2.5 text-xs font-bold hover:bg-slate-800"
            >
              <Download className="w-3.5 h-3.5" /> Download MP4
            </a>
            <a
              href="/vendors"
              target="_blank"
              rel="noreferrer"
              data-testid="vendor-video-view-live"
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2.5 text-xs font-semibold"
            >
              <Eye className="w-3.5 h-3.5" /> View on /vendors
            </a>
            <div className="text-[11px] text-slate-500 leading-snug mt-2">
              The film auto-publishes to <span className="font-mono">/vendors</span> as soon
              as a successful render completes — no separate publish step required.
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
